import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {trackEventWithModule} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CreateDropdownOption} from 'file-browser/CreateDropdownConstants';
import {ActionOrigin, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import navigateToURL from 'lib/navigateToURL';
import {CreateMenuItem} from 'nav/navbar/CreateMenuConstants';
import {getEnabledStudyTypes, getStudyTypeText} from 'studies/TypedStudyUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {assertNever} from 'util/Types';

import {CreateStudyMenuQuery} from './__generated/CreateStudyMenuQuery';
import CreateStudyModal from './CreateStudyModal';
import {CreateStudyModalOptionalParams} from './NewStudyDropdown';

type CreateStudyMenuQueryStudySchema = CreateStudyMenuQuery['studySchemas'][number];

export type StudySchemaForCreate = CreateStudyMenuQueryStudySchema;

export const CREATE_STUDY_MENU_QUERY: DocumentNode = gql`
  query CreateStudyMenuQuery @skipAudit @engComponent(name: STUDIES) {
    studySchemas {
      id
      name
      studyType
      archiveRecord {
        purpose
      }
      ... on PoliceableInterface {
        id
        permissions {
          ...SchemaPermissionsGraphQL
        }
      }
    }
  }
  ${getFragmentWrapper('SchemaPermissions').fragment}
`;

/**
 * An intermediate representation that can be mapped to CreateDropdownOption or CreateMenuItem.
 */
interface StudySchemaOption {
  name: string;
  value: string;
  studyType?: StudyType;
  disabled?: boolean;
  icon?: BenchlingIconName;
  onClick?: () => void;
}

const LOADING: StudySchemaOption = {
  name: 'Loading...',
  value: 'study-schema-loading',
  disabled: true,
};
/*
 * @package exported for testing
 */
export const STUDY_SCHEMA_CONFIG_ERROR_OPTION: StudySchemaOption = {
  name: 'Studies is not enabled. Contact Benchling support for help.',
  value: 'study-schema-config-error',
  onClick: () => {
    window.open('mailto:support@benchling.com');
  },
  icon: 'warning',
};
export const STUDY_SCHEMA_ERROR_OPTION: StudySchemaOption = {
  name: 'Error loading study schemas',
  value: 'study-schema-error',
  disabled: true,
  icon: 'error',
};

export const STUDY_SCHEMA_NONE_OPTION_ID = 'study-schema-none';

/** @package */
export function useStudySchemaOptions(
  actionOrigin: ActionOrigin,
  params: CreateStudyModalOptionalParams,
  studyType?: StudyType
): Array<StudySchemaOption> {
  const result = useSkippableBenchlingQuery<CreateStudyMenuQuery>(CREATE_STUDY_MENU_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !BenchlingConfig.STUDIES_ENABLE,
  });
  switch (result.type) {
    case QueryResultType.PENDING:
      return [LOADING];
    case QueryResultType.SKIPPED:
      return [STUDY_SCHEMA_CONFIG_ERROR_OPTION];
    case QueryResultType.ERROR:
      return [STUDY_SCHEMA_ERROR_OPTION];
    case QueryResultType.SUCCESS: {
      const readableSchemas = result.data.studySchemas.filter(
        (schema) => !isArchived(schema) && (studyType == null || schema.studyType === studyType)
      );

      const creatableSchemas = readableSchemas.filter(
        (schema): boolean =>
          Boolean(schema.permissions.objectsCreatable) && getEnabledStudyTypes().includes(schema.studyType)
      );
      const noSchemasMessage =
        readableSchemas.length === 0
          ? `No study schemas ${
              studyType ? `with type ${getStudyTypeText(studyType)} ` : ''
            }have been configured.`
          : `You do not have permission to create a study for any existing ${
              studyType ? `${getStudyTypeText(studyType)} ` : ''
            }study schema.`;

      return creatableSchemas.length === 0
        ? [
            {
              name: noSchemasMessage,
              value: STUDY_SCHEMA_NONE_OPTION_ID,
              onClick: () => {
                navigateToURL('/study-schemas/listing');
              },
              icon: 'warning',
            },
          ]
        : creatableSchemas.map((studySchema): StudySchemaOption => ({
            name: studySchema.name,
            onClick: () => {
              trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.STUDIES, {
                modalType: 'create_study_modal',
                studyType: studySchema.studyType,
              });
              legacyRunModal(CreateStudyModal, {
                studySchemaId: studySchema.id,
                actionOrigin,
                initialValues: params || {},
              });
            },
            value: studySchema.id,
            studyType: studySchema.studyType,
          }));
    }
    default:
      assertAndContinue(false, `Unexpected result type: ${assertNever(result).type}`);
      return [];
  }
}

export function useStudySchemasAsCreateDropdownOptions(
  actionOrigin: ActionOrigin,
  params: CreateStudyModalOptionalParams,
  studyType?: StudyType
): Array<CreateDropdownOption> {
  return useStudySchemaOptions(actionOrigin, params, studyType).map(
    (item: StudySchemaOption): CreateDropdownOption => ({
      name: item.name,
      icon: item.icon,
      value: item.value,
      disabled: item.disabled,
      handleClick: item.onClick,
    })
  );
}

export function useStudySchemasAsCreateMenuItems(
  folderId: string | null,
  actionOrigin: ActionOrigin
): Array<CreateMenuItem> {
  return useStudySchemaOptions(actionOrigin, {initialFolderId: folderId}).map((item: StudySchemaOption) => ({
    name: item.name,
    icon: item.icon,
    disabled: item.disabled,
    handleOnClick: item.onClick,
    value: item.value,
  }));
}
